import { useEffect, useRef, useState } from "react";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import { CheckboxComponent, TextFieldComponent } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import { traceo } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { useTranslation } from "react-i18next";
import ProjectService from "../services/ProjectService";
import Project from "../models/Project";
import { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import { MOBILE_WIDTH, ROWS_PER_PAGE } from "../App";
import { AppToolbar } from "../components/AppToolbar";
import RoleUtils from "../utils/RoleUtils";
import UserService from "../services/UserService";
import { AppDialog } from "../components/AppDialog";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import useLocalStorage, { removeItem, STORAGE_KEYS } from "../hooks/useLocalStorage";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import { Typography, makeStyles } from "@material-ui/core";
import { Box, Button, Grid } from "@mui/material";
import { ModalAddRemovePM } from "../components/ModalAddRemovePM";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import { SwitchCustom } from "../utils/SwitchCustom";
import { SelectComponent } from "../components/SelectComponent";
import useTableHeight from "../hooks/useTableHeight";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import useUpdateUrl from "../hooks/useUpdateUrl";

import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { ColorUtils } from "../utils/ColorUtils";
import { useStyles as formStyles } from "../components/Main/formStyles";

enum ProjectType {
  internal = "Internal",
  external = "External",
  both = "Internal + External",
}

function AssignedManagerCellComponent(props: { projectExternalId: string; forceUpdateOn?: number }): JSX.Element {
  const defaultStringToShow: string = "-";
  const [managerListToShow, setManagerListToShow] = useState<string>(defaultStringToShow);

  useEffect(() => {
    ProjectService.getManagerListFromProject(props.projectExternalId)
      .then((response) => {
        const managerList: EnsolversOpsUser[] = response.getContent();

        if (managerList.length === 0) {
          setManagerListToShow(defaultStringToShow);
        } else {
          setManagerListToShow(managerList.map((value) => value.firstName + " " + value.lastName.slice(0, 1)).join(", "));
        }
      })
      .catch((error) => traceo("Error getting manager list from project.", error, null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.projectExternalId, props.forceUpdateOn]);

  return <>{managerListToShow}</>;
}

export default function ProjectList() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);
  const [selectedEditProject, setSelectedEditProject] = useState<Project | undefined>(undefined);
  const [editProjectManager, setEditProjectManager] = useState<Project | undefined>(undefined);
  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [userNames, setUserNames] = useState<AutoCompleteUIDTO[]>([]);
  const [managerList, setManagerList] = useState<EnsolversOpsUser[] | undefined>(undefined);
  const [searchManager, setSearchManager] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [projectsAssignedToPm, setProjectsAssignedToPm] = useState<Project[] | undefined>(undefined);
  const [projectAssigned, setProjectAssigned] = useState<AutoCompleteUIDTO[]>([]);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showArchivedProjects, setShowArchivedProjects] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_SHOW_ARCHIVED_PROJECTS, false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [projectType, setProjectType] = useState<string>(ProjectType.both);
  const hasMounted = useRef(false);
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const projectTypeFilter = searchParams.get("type");
  const assignedToMe = searchParams.get("assigned");
  const archived = searchParams.get("archived");
  const {updateUrl} = useUpdateUrl(Routes.PROJECTS, searchParams);

  const [showDialog, setShowDialog] = useState(true);
  const customStyles = formStyles();

  useEffect(() => {
    if (RoleUtils.isAdmin()) {
      UserService.findManagerUserNames()
        .then((response) => setUserNames(response.getContentOrThrowError()))
        .catch((error) => traceo("Error getting user names.", error, null));
    }

    if (RoleUtils.isManager()) {
      ProjectService.getProjectsFromManager()
        .then((response) => setProjectsAssignedToPm(response.getContent()))
        .catch((error) => traceo("Error getting assigned projects for manager", error, null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (projectTypeFilter !== null) {
      setProjectType(projectTypeFilter);
    } else {
      searchParams.set("type", ProjectType.both);
    }

    if (archived !== null) {
      setShowArchivedProjects(archived === "true");
    } else if (showArchivedProjects !== undefined) {
      searchParams.set("archived", showArchivedProjects.toString());
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (RoleUtils.isManager() && assigned === true) {
      ProjectService.findProjectsNamesByUserManagerExternalId()
        .then((response) => setProjectAssigned(response.getContent()))
        .catch((error) => traceo("Error getting projects", error, null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned]);

  useEffect(() => {
    if (editProjectManager && editProjectManager.externalId) {
      ProjectService.getManagerListFromProject(editProjectManager.externalId)
        .then((response) => setManagerList(response.getContent()))
        .catch((error) => traceo("Error getting project managers.", error, null));
    }
  }, [editProjectManager]);

  const handleOnDeleteProject = async () => {
    if (!selectedProject) return;

    ProjectService.delete(selectedProject)
      .then(() => {
        setSelectedProject(undefined);
        setUpdate(Date.now());
        NotificationService.info("Project deleted successfully");
      })
      .catch((err) => NotificationService.error(err.response.data.message));
  };

  const updateStoredProjectInStorage = (project: Project) => {
    setStoredProject({ externalId: project.externalId || "", label: project.name });
  };

  const handleCloseManagerModal = () => {
    setEditProjectManager(undefined);
    setSearchManager(undefined);
  };

  const getAdditionalActions = (instance: Project): ActionsMenuEntryIcons[] => {
    const options = RoleUtils.isAdmin() ? [
      {
        title: t("Assign PM"),
        action: () => setEditProjectManager(instance),
        icon:<PersonOutlineIcon/>
      },
      {
        title: t("Delete"),
        action: () => {
          setSelectedProject(instance);
          removeItem(STORAGE_KEYS.STORED_PROJECT);
        },
        icon:<DeleteOutlineIcon/>
      },
      {
        title: t("Edit"),
        action: () => {
          setIsEditing(true);
          setSelectedEditProject(instance);
        },
        icon:<EditIcon/>
      },
    ]
    : []

    return options;
  };

  const projectIsAssignedToPm = (externalId: string): boolean => {
    return (
      !!projectsAssignedToPm &&
      projectsAssignedToPm.map((project) => project.externalId).find((projectExtId) => projectExtId === externalId) !== undefined
    );
  };

  const handleChangeAssignedProjectsByUser = () => {
    setAssigned(!assigned);
    searchParams.set("assigned", (!assigned).toString());
    updateUrl();
  };

  const toggleShowArchivedProjects = () => {
    setShowArchivedProjects((prev) => !prev);
    searchParams.set("archived", (!showArchivedProjects).toString());
    updateUrl();
  };

  const handleDataFetch = (pageNumber: number, count: number, sortBy?: string | undefined, sortDir?: string | undefined) => {
    if (assigned) {
      return ProjectService.getAllProjectsByManagerExternalId(pageNumber, count, {}, showArchivedProjects, projectType)
        .then((result) => result.getContent())
        .finally(() => setIsLoading(false));
    }
    return ProjectService.findAll(pageNumber, count, showArchivedProjects, projectType)
      .then((result) => result.getContent())
      .finally(() => setIsLoading(false));
  };

  function handleOnCancel(): void {
    setIsEditing(false)
  }

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <>
          <AppToolbar title="Projects">
            <div className={classes.container}>
              <SelectComponent
                label={"Project Type"}
                value={projectType}
                setValue={(value) => {
                  setProjectType(value);
                  searchParams.set("type", value);
                  updateUrl();
                }}
                options={Object.values(ProjectType)}
                size="small"
              />
              <div>
                <SwitchCustom
                  checked={assigned}
                  onChange={handleChangeAssignedProjectsByUser}
                  size="medium"
                  color="primary"
                  label="Assigned to me"
                />
                <SwitchCustom
                  checked={showArchivedProjects}
                  onChange={toggleShowArchivedProjects}
                  size="medium"
                  color="primary"
                  label="Archived"
                />
              </div>
            </div>
          </AppToolbar>
        </>
      }
      bottomSection={
        <Box sx={{ boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" }}>
          <SpinnerWrapper isLoading={isLoading}>
            <TableBasedCrud<Project>
              headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
              buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
              cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
              dialogStyles={ customStyles.root }
              buttonContainer={ customButtonsContainer }
              gridButtonContainer={ gridButtonContainer }
              addButtonBuilder={ customButtonBuilder }
              maxHeight={calculateMaxHeight(RoleUtils.isAdmin())}
              stickyHeader
              stickyPagination
              elementName={"Project"}
              rowsPerPage={ROWS_PER_PAGE}
              forceUpdateOn={update}
              enablePagination
              denseTable
              stylesForRowIfCondition={(instance) => (instance.archived ? { backgroundColor: "#F1F0F0" } : {})}
              onDataFetch={handleDataFetch}
              onCreate={
                RoleUtils.isAdmin()
                  ? (instance) => {
                    ProjectService.create(instance)
                      .then((data) => {
                        updateStoredProjectInStorage(data.getContent());
                        NotificationService.info("Project created successfully");
                      })
                      .catch((error) => NotificationService.error("Error creating a project, the project key must be unique"));
                  }
                  : undefined
              }
              onUpdate={
                RoleUtils.isAdminOrManager()
                  ? (instance) => {
                    setIsEditing(false);
                    instance.externalId && (RoleUtils.isAdmin() || projectIsAssignedToPm(instance.externalId))
                      ? ProjectService.update(instance)
                        .then((data) => {
                          updateStoredProjectInStorage(data.getContent());
                          NotificationService.info("Project edited successfully");
                        })
                        .catch((error) => NotificationService.error(error.response.data.message))
                      : NotificationService.error(
                        `Error updating a project, the project is not assigned and managed for user: ${AuthService.getUser()?.firstName
                        } ${AuthService.getUser()?.lastName}`
                      );
                  }
                  : undefined
              }
              onCancelDialog={() => setIsEditing(false)}
              onClickEdit={() => setIsEditing(true)}
              renderDialogContent={(register, errors, control) => (
                <>
                  <TextFieldComponent propertyName={"name"} label="Name" required={true} />
                  <TextFieldComponent
                    propertyName={"key"}
                    label="Key"
                    required={true}
                    pattern={/^[A-Za-z0-9_]+$/}
                    customHelperText={"Key should be provided. Valid characters: A-Z a-z 0-9 _"}
                  />
                  <TextFieldComponent propertyName={"slackChannelId"} label="Internal Slack Channel Id" required={false} />
                  <TextFieldComponent propertyName={"externalSlackChannelId"} label="External Slack Channel Id" required={false} />
                  <Grid container display={"flex"} alignItems={"center"} justifyItems={"center"}>
                    <Grid item xs={12} sm={6}>
                      <CheckboxComponent
                        control={control}
                        register={register}
                        errors={errors}
                        propertyName={"omitHoursLimit"}
                        label={"Omit Hours Limit"}
                        required={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CheckboxComponent
                        control={control}
                        register={register}
                        errors={errors}
                        propertyName={"internal"}
                        label={"Internal"}
                        required={false}
                      />
                    </Grid>
                    {isEditing && (
                      <Grid item xs={12} sm={6}>
                        <CheckboxComponent
                          control={control}
                          register={register}
                          errors={errors}
                          propertyName={"archived"}
                          label={"Archived"}
                          required={false}
                        />
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
              tableColumns={[
                {
                  header: "Name",
                  content: (instance) => <>{instance.name}</>,
                },
                {
                  header: "Key",
                  content: (instance) => <>{instance.key}</>,
                },
                { header: "Internal Slack Channel Id", content: (instance) => <>{instance.slackChannelId}</> },
                {
                  header: "External Slack Channel Id",
                  content: (instance) => <>{instance.externalSlackChannelId}</>,
                },
                {
                  header: "Omit hours limit",
                  content: (instance) => <>{instance.omitHoursLimit ? "Yes" : "No"}</>,
                },
                {
                  header: "Internal",
                  content: (instance) => <>{instance.internal ? "Yes" : "No"}</>,
                },
                {
                  header: "Archived",
                  content: (instance) => <>{instance.archived ? "Yes" : "No"}</>,
                },
                {
                  header: "Assigned Managers",
                  content: (instance) => (
                    <AssignedManagerCellComponent projectExternalId={instance.externalId || ""} forceUpdateOn={update} />
                  ),
                },
                {
                  header: t("Actions"),
                  content: (instance) => {
                    const actions = getAdditionalActions(instance)
                    return (<>
                      {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                      </>
                    )
                  },
                },
              ]}
              hideActions={true}  // Hide default Actions column
              buildAdditionalActions={
                RoleUtils.isAdmin()
                  ? (instance, refresh) => {
                    return [
                      {
                        title: t("Assign PM"),
                        action: () => setEditProjectManager(instance),
                      },
                      {
                        title: t("Delete"),
                        action: () => {
                          setSelectedProject(instance);
                          removeItem(STORAGE_KEYS.STORED_PROJECT);
                        },
                      },
                    ];
                  }
                  : undefined
              }
              resetValue={{ name: "", key: "", slackChannelId: "", externalSlackChannelId: "", phone: "", omitHoursLimit: false }}
            />
          </SpinnerWrapper>
          {/* Dialog to delete projects */}
          <AppDialog
            open={!!selectedProject}
            onClose={() => setSelectedProject(undefined)}
            title={"Delete"}
            content={
              <Typography>
                {`The next project is going to be eliminated: ${selectedProject?.name}. 
            Do you want to confirm this action?`}
              </Typography>
            }
            buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteProject() }]}
          />
          {/* Dialog to list, add and remove assigned Project Managers */}
          <ModalAddRemovePM
            editProjectManager={editProjectManager}
            handleCloseManagerModal={handleCloseManagerModal}
            managerList={managerList}
            setManagerList={setManagerList}
            setUpdate={setUpdate}
            setSearchManager={setSearchManager}
            searchManager={searchManager}
            userNames={userNames}
          />
          {/* Copy of the Dialog to Edit -- To use with Edit Action Icon */}
          <EditDialog<Project>
            headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
            buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
            cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
            dialogStyles={ customStyles.root }              
            elementName={"Project"}
            dialogPosition={DialogPosition.Center}
            currentInstance={selectedEditProject}
            newItemDialogOpen={isEditing}
            onUpdate={
              RoleUtils.isAdminOrManager()
                ? (instance) => {
                  setIsEditing(false);
                  instance.externalId && (RoleUtils.isAdmin() || projectIsAssignedToPm(instance.externalId))
                    ? ProjectService.update(instance)
                      .then((data) => {
                        updateStoredProjectInStorage(data.getContent());
                        NotificationService.info("Project edited successfully");
                      })
                      .catch((error) => NotificationService.error(error.response.data.message))
                    : NotificationService.error(
                      `Error updating a project, the project is not assigned and managed for user: ${AuthService.getUser()?.firstName
                      } ${AuthService.getUser()?.lastName}`
                    );
                }
                : undefined
            }
            onCreate={
              RoleUtils.isAdmin()
                ? (instance) => {
                  ProjectService.create(instance)
                    .then((data) => {
                      updateStoredProjectInStorage(data.getContent());
                      NotificationService.info("Project created successfully");
                    })
                    .catch((error) => NotificationService.error("Error creating a project, the project key must be unique"));
                }
                : undefined
            }
            onCancel={handleOnCancel}
            renderDialogContent={(register, errors, control) => (
            <>
              <TextFieldComponent propertyName={"name"} label="Name" required={true} />
              <TextFieldComponent
                propertyName={"key"}
                label="Key"
                required={true}
                pattern={/^[A-Za-z0-9_]+$/}
                customHelperText={"Key should be provided. Valid characters: A-Z a-z 0-9 _"}
              />
              <TextFieldComponent propertyName={"slackChannelId"} label="Internal Slack Channel Id" required={false} />
              <TextFieldComponent propertyName={"externalSlackChannelId"} label="External Slack Channel Id" required={false} />
              <Grid container display={"flex"} alignItems={"center"} justifyItems={"center"}>
                <Grid item xs={12} sm={6}>
                  <CheckboxComponent
                    control={control}
                    register={register}
                    errors={errors}
                    propertyName={"omitHoursLimit"}
                    label={"Omit Hours Limit"}
                    required={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CheckboxComponent
                    control={control}
                    register={register}
                    errors={errors}
                    propertyName={"internal"}
                    label={"Internal"}
                    required={false}
                  />
                </Grid>
                {isEditing && (
                  <Grid item xs={12} sm={6}>
                    <CheckboxComponent
                      control={control}
                      register={register}
                      errors={errors}
                      propertyName={"archived"}
                      label={"Archived"}
                      required={false}
                    />
                  </Grid>
                )}
              </Grid>
            </> )}
            setNewInstanceDialogOpen={() => {}}
            setLastUpdateTimestamp={() => {}}
            />
        </Box>
      }
    />
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    "& > div:last-child": {
      display: "flex",
      flexDirection: "row",
    },
    "& > div:first-child": {
      margin: "0 !important",
    },
    columnGap: "1.5rem",
    borderRadius: "4px",
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)"   
  },
  [theme.breakpoints.down(MOBILE_WIDTH)]: {
    container: {
      flexDirection: "column",
      rowGap: "10px",
    },
  },
}),
{ index: 1 }
);
